import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form, FormItem, Input } from "@clarkinc/zest-design-system/ui";
import Calculator from "../../../components/Calculator";

const schema = z.object({
  cost: z.coerce
    .number({ message: "Must be a number greater than 0" })
    .gt(0, "Must be a number greater than 0"),
  price: z.coerce
    .number({ message: "Must be a number greater than 0" })
    .gt(0, "Must be a number greater than 0"),
  servings: z.coerce
    .number({ message: "Must be a number greater than 0" })
    .gt(0, "Must be a number greater than 0"),
});

interface Props {
  defaultTitle?: string;
  defaultCost?: string;
  defaultPrice?: string;
  defaultServings?: string;
}

type FormFields = z.infer<typeof schema>;

function ProfitCalculator({
  defaultTitle = "Generated Profits Calculator",
  defaultCost = "0.26",
  defaultPrice = "1",
  defaultServings = "20",
}: Props) {
  const [dayProfits, setDayProfits] = useState("");
  const [monthProfits, setMonthProfits] = useState("");
  const [yearProfits, setYearProfits] = useState("");

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const resetForm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    resetProfits();
    form.reset();
  };

  const resetProfits = () => {
    setDayProfits("");
    setMonthProfits("");
    setYearProfits("");
  };

  const handleSubmit: SubmitHandler<FormFields> = (data) => {
    const costValue = data.cost;
    const priceValue = data.price;
    const servingsValue = data.servings;
    const profitsPerUnit = (priceValue - costValue) * servingsValue;

    if (!Number.isNaN(profitsPerUnit)) {
      setDayProfits(formatAsCurrency.formatIfUndefined(profitsPerUnit));

      setMonthProfits(formatAsCurrency.formatIfUndefined(profitsPerUnit * 30));

      setYearProfits(
        formatAsCurrency.formatIfUndefined(profitsPerUnit * 30 * 12),
      );
    }
  };

  return (
    <div className="bg-gray-100 border-gray-200 border-solid border leading-6 mx-auto text-left w-full rounded">
      <div className="border-gray-200 border-solid border border-t-0 border-r-0 border-l-0 border-b px-5 py-3">
        <h4 className="text-lg leading-7 m-0 [text-shadow:0_1px_0_#fff]">
          {defaultTitle}
        </h4>
      </div>
      <Form
        form={form}
        onSubmit={form.handleSubmit(handleSubmit)}
        aria-label="Profit Calculator"
        className="px-5 pb-5"
        id="profit-calculator-form"
        theme="wss"
      >
        <div className="text-xl pt-5 pb-6 [text-shadow:0_1px_0_white]">
          <div data-testid="input-container" className="flex flex-col gap-y-4">
            <FormItem name="cost" required>
              <Input
                label="Unit Cost"
                inputSize="large"
                data-testid="cost"
                placeholder="Enter cost"
                defaultValue={defaultCost}
              />
            </FormItem>
            <FormItem name="price" required>
              <Input
                label="Retail Price"
                inputSize="large"
                data-testid="price"
                placeholder="Enter price"
                defaultValue={defaultPrice}
              />
            </FormItem>
            <FormItem name="servings" required>
              <Input
                label="Servings Per Day"
                inputSize="large"
                data-testid="servings"
                placeholder="Enter price"
                defaultValue={defaultServings}
              />
            </FormItem>
          </div>

          <hr className="border-0 h-0.5 bg-gray-200 my-4" />

          <div>
            <Calculator.ResultContainer>
              <Calculator.ResultTitle>Profit Per Day</Calculator.ResultTitle>
              <Calculator.ResultText data-testid="dayProfit">
                {dayProfits}
              </Calculator.ResultText>
            </Calculator.ResultContainer>
            <Calculator.ResultContainer>
              <Calculator.ResultTitle>Profit Per Month</Calculator.ResultTitle>
              <Calculator.ResultText data-testid="monthProfit">
                {monthProfits}
              </Calculator.ResultText>
            </Calculator.ResultContainer>
            <Calculator.ResultContainer>
              <Calculator.ResultTitle>Profit Per Year</Calculator.ResultTitle>
              <Calculator.ResultText data-testid="yearProfit">
                {yearProfits}
              </Calculator.ResultText>
            </Calculator.ResultContainer>
          </div>
        </div>
        <div className="flex flex-col items-center gap-3 xm:flex-row xm:justify-between xm:gap-0 xm:hidden">
          <Button
            type="submit"
            theme="wss"
            size="large"
            className="w-full xm:w-auto"
            form="profit-calculator-form"
          >
            Calculate
          </Button>
          <Button
            type="button"
            theme="wss"
            size="large"
            className="w-full xm:w-auto"
            variant="secondary"
            onClick={resetForm}
          >
            Reset
          </Button>
        </div>
        <div className="hidden gap-3 justify-end xm:flex">
          <Button
            type="button"
            theme="wss"
            size="large"
            className="w-full xm:w-auto"
            variant="secondary"
            data-testid="profit-reset-button-desktop"
            onClick={resetForm}
          >
            Reset
          </Button>
          <Button
            type="submit"
            theme="wss"
            size="large"
            className="w-full xm:w-auto"
            form="profit-calculator-form"
            data-testid="profit-calculate-button-desktop"
          >
            Calculate
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default ProfitCalculator;
